import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  getPwaStats, getPushSubscribersCount, getPushSubscribers, getPushNotifications,
  createPushNotification, uploadAdminFile, getTareas, getTests, TEST_TIPOS,
} from '../../api/adminClient';

const BODY_MAX_LEN = 150;

const DEVICE_LABELS = { mobile: 'Móvil', tablet: 'Tablet', desktop: 'Escritorio', unknown: 'Desconocido' };
const STATUS_LABELS = { installed: 'Instalado', uninstalled: 'Desinstalado' };

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-3xl font-bold text-slate-800">{value}</p>
      {sub && <p className="text-sm text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

const PREVIEW_APP_ICON = '/icons/icon-96.png';
const PREVIEW_SITE = 'labpsi.mdp.edu.ar';

function PreviewFrame({ label, children }) {
  return (
    <div>
      <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">{label}</p>
      {children}
    </div>
  );
}

function ActionButtonsRow({ actions, variant }) {
  const visible = (actions || []).filter((a) => a.title.trim());
  if (visible.length === 0) return null;
  const cls = variant === 'chrome'
    ? 'text-[11px] font-medium text-blue-600 hover:underline'
    : variant === 'android'
      ? 'text-[11px] font-semibold text-violet-700 uppercase tracking-wide'
      : 'text-[11px] font-medium text-blue-500';
  return (
    <div className="flex gap-4 mt-2 pt-2 border-t border-slate-100">
      {visible.map((a, i) => <span key={i} className={cls}>{a.title}</span>)}
    </div>
  );
}

function ChromePreview({ title, body, image, actions }) {
  return (
    <div className="rounded-lg shadow-md border border-slate-200 bg-white overflow-hidden font-sans">
      <div className="flex items-center gap-2 px-3 pt-2.5 pb-1">
        <img src={PREVIEW_APP_ICON} className="w-4 h-4 rounded-sm" alt="" />
        <span className="text-[11px] text-slate-500 flex-1 truncate">{PREVIEW_SITE}</span>
        <span className="text-slate-300 text-xs leading-none">✕</span>
      </div>
      <div className="px-3 pb-3">
        <div className="flex gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-slate-800 truncate">{title || 'Título de la notificación'}</p>
            <p className="text-[12px] text-slate-600 line-clamp-2 mt-0.5">{body || 'Así se va a ver el mensaje.'}</p>
          </div>
          {image && <img src={image} className="w-14 h-14 rounded object-cover flex-shrink-0" alt="" />}
        </div>
        <ActionButtonsRow actions={actions} variant="chrome" />
      </div>
    </div>
  );
}

function AndroidPreview({ title, body, image, actions }) {
  return (
    <div className="rounded-2xl shadow-md bg-white overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 pt-2.5">
        <img src={PREVIEW_APP_ICON} className="w-4 h-4 rounded-full" alt="" />
        <span className="text-[11px] text-slate-500">LABPSI</span>
        <span className="text-[11px] text-slate-400">· ahora</span>
      </div>
      <div className="px-3 pt-1 pb-3">
        <p className="text-[13px] font-medium text-slate-900 truncate">{title || 'Título de la notificación'}</p>
        <p className="text-[12px] text-slate-600 line-clamp-2">{body || 'Así se va a ver el mensaje.'}</p>
        {!image && <ActionButtonsRow actions={actions} variant="android" />}
      </div>
      {image && <img src={image} className="w-full h-28 object-cover" alt="" />}
      {image && <div className="px-3 pb-2.5"><ActionButtonsRow actions={actions} variant="android" /></div>}
    </div>
  );
}

function IosPreview({ title, body, image, actions }) {
  return (
    <div className="rounded-2xl shadow-md bg-slate-100 overflow-hidden px-3 py-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <img src={PREVIEW_APP_ICON} className="w-4 h-4 rounded-[4px]" alt="" />
        <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">LABPSI</span>
        <span className="text-[10px] text-slate-400">ahora</span>
      </div>
      <div className="flex gap-2 items-start">
        <div className="flex-1 min-w-0">
          <p className="text-[13px] font-semibold text-slate-900 truncate">{title || 'Título de la notificación'}</p>
          <p className="text-[12px] text-slate-700 line-clamp-2">{body || 'Así se va a ver el mensaje.'}</p>
        </div>
        {image && <img src={image} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" alt="" />}
      </div>
      <ActionButtonsRow actions={actions} variant="ios" />
    </div>
  );
}

function formatFecha(fecha) {
  if (!fecha) return '—';
  const [datePart, timePart] = fecha.split(' ');
  const [y, m, d] = datePart.split('-');
  return `${d}/${m}/${y}${timePart ? ' ' + timePart.slice(0, 5) : ''}`;
}

export default function AdminPwaInstalaciones() {
  const [tab, setTab] = useState('resumen');

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-slate-800">Instalaciones de la PWA</h1>
      </div>

      <div className="flex gap-1 mb-6 border-b border-slate-200">
        {[
          { key: 'resumen', label: 'Resumen' },
          { key: 'push', label: 'Notificaciones push' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px cursor-pointer ${
              tab === t.key ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'resumen' ? <ResumenTab /> : <PushTab />}
    </div>
  );
}

function ResumenTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const d = await getPwaStats();
      setData(d);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-slate-500">Cargando instalaciones…</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;

  const { resumen, recientes } = data;
  const dispTotal = Object.values(resumen.por_dispositivo).reduce((a, b) => a + b, 0);

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button
          onClick={load}
          className="text-sm px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-50 cursor-pointer"
        >
          Actualizar
        </button>
      </div>

      {/* ── Tarjetas resumen ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <StatCard label="Instalaciones activas" value={resumen.instaladas_activas.toLocaleString()} />
        <StatCard label="Desinstalaciones (inferidas)" value={resumen.desinstaladas.toLocaleString()} />
        <StatCard label="Total histórico" value={resumen.total_historico.toLocaleString()} />
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Activas por dispositivo</p>
          {dispTotal === 0 ? (
            <p className="text-slate-400 text-sm">Sin datos</p>
          ) : (
            <ul className="space-y-1">
              {Object.entries(resumen.por_dispositivo).map(([k, v]) => (
                <li key={k} className="flex justify-between text-sm">
                  <span className="text-slate-600">{DEVICE_LABELS[k] ?? k}</span>
                  <span className="font-medium text-slate-800">
                    {v} <span className="text-slate-400 font-normal">({Math.round(v / dispTotal * 100)}%)</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-500 mb-6">
        No existe un evento de "desinstalada" en los navegadores: un dispositivo se marca como desinstalado
        cuando deja de abrir la app en modo standalone durante {resumen.umbral_dias} días. Es una estimación,
        no un dato exacto.
      </p>

      {/* ── Tabla de dispositivos ── */}
      {recientes.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
          No hay instalaciones registradas todavía.
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-slate-600">ID</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Dispositivo</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">SO</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">IP</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Estado</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Instalado</th>
                <th className="px-4 py-3 text-left font-medium text-slate-600">Última actividad</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recientes.map((r) => (
                <tr key={r.device_id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3 font-mono text-xs text-slate-400" title={r.device_id}>{r.device_id.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-slate-700">{DEVICE_LABELS[r.device_type] ?? r.device_type}</td>
                  <td className="px-4 py-3 text-slate-600">{r.os ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{r.ip_address ?? '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${r.status === 'installed' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                      {STATUS_LABELS[r.status] ?? r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500">{formatFecha(r.installed_at)}</td>
                  <td className="px-4 py-3 text-slate-500">{formatFecha(r.last_seen_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  );
}

function DestinoUrlPicker({ tareaList, onSelect }) {
  const [selLenguaje, setSelLenguaje] = useState(null);
  const [selCategoria, setSelCategoria] = useState(null);
  const [selTarea, setSelTarea] = useState(null);
  const [selTestKey, setSelTestKey] = useState('');
  const [tests, setTests] = useState(null);
  const [loadingTests, setLoadingTests] = useState(false);

  const lenguajes = useMemo(() => {
    const seen = new Map();
    tareaList.forEach((t) => { if (!seen.has(t.id_lenguaje)) seen.set(t.id_lenguaje, t.lenguaje); });
    return [...seen.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [tareaList]);

  const categorias = useMemo(() => {
    if (!selLenguaje) return [];
    const seen = new Map();
    tareaList.filter((t) => t.id_lenguaje === selLenguaje.id)
      .forEach((t) => { if (!seen.has(t.id_tipotest)) seen.set(t.id_tipotest, t.categoria); });
    return [...seen.entries()].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label, 'es'));
  }, [tareaList, selLenguaje]);

  const tareas = useMemo(() => {
    if (!selCategoria) return [];
    return tareaList.filter((t) => t.id_tipotest === selCategoria.id)
      .sort((a, b) => a.Descripcion.localeCompare(b.Descripcion, 'es'));
  }, [tareaList, selCategoria]);

  const handleLenguajeChange = (e) => {
    const id = Number(e.target.value) || null;
    setSelCategoria(null); setSelTarea(null); setTests(null); setSelTestKey('');
    const l = id ? lenguajes.find((x) => x.id === id) : null;
    setSelLenguaje(l);
    onSelect(l ? `/lenguaje/${l.id}` : '');
  };

  const handleCategoriaChange = (e) => {
    const id = Number(e.target.value) || null;
    setSelTarea(null); setTests(null); setSelTestKey('');
    const c = id ? categorias.find((x) => x.id === id) : null;
    setSelCategoria(c);
    onSelect(c ? `/lenguaje/${selLenguaje.id}/categoria/${c.id}` : `/lenguaje/${selLenguaje.id}`);
  };

  const handleTareaChange = async (e) => {
    const id = Number(e.target.value) || null;
    setTests(null); setSelTestKey('');
    if (!id) {
      setSelTarea(null);
      onSelect(`/lenguaje/${selLenguaje.id}/categoria/${selCategoria.id}`);
      return;
    }
    const t = tareas.find((x) => x.ID === id);
    setSelTarea({ id: t.ID, label: t.Descripcion });
    onSelect(`/lenguaje/${selLenguaje.id}/categoria/${selCategoria.id}/tarea/${t.ID}`);
    setLoadingTests(true);
    try {
      const results = await Promise.all(
        TEST_TIPOS.map((tt) =>
          getTests(tt.key, t.ID)
            .then((rows) => rows.filter((r) => !r.baja).map((r) => ({ ...r, _tipoKey: tt.key })))
            .catch(() => [])
        )
      );
      setTests(results.flat());
    } finally {
      setLoadingTests(false);
    }
  };

  const handleTestChange = (e) => {
    const val = e.target.value;
    setSelTestKey(val);
    if (!val) {
      onSelect(`/lenguaje/${selLenguaje.id}/categoria/${selCategoria.id}/tarea/${selTarea.id}`);
      return;
    }
    const [tipoKey, id] = val.split('-');
    onSelect(`/lenguaje/${selLenguaje.id}/categoria/${selCategoria.id}/tarea/${selTarea.id}/test/${tipoKey}/${id}`);
  };

  const selectCls = 'w-full border border-slate-300 rounded px-2 py-1.5 text-xs text-slate-600 bg-white';

  return (
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 grid gap-2">
      <select value={selLenguaje?.id ?? ''} onChange={handleLenguajeChange} className={selectCls}>
        <option value="">Elegí un acceso…</option>
        {lenguajes.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
      </select>

      {selLenguaje && (
        <select value={selCategoria?.id ?? ''} onChange={handleCategoriaChange} className={selectCls}>
          <option value="">Elegí una categoría…</option>
          {categorias.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      )}

      {selCategoria && (
        <select value={selTarea?.id ?? ''} onChange={handleTareaChange} className={selectCls}>
          <option value="">Elegí una tarea…</option>
          {tareas.map((t) => <option key={t.ID} value={t.ID}>{t.Descripcion}</option>)}
        </select>
      )}

      {selTarea && (
        loadingTests ? (
          <p className="text-xs text-slate-400">Cargando tests…</p>
        ) : !tests || tests.length === 0 ? (
          <p className="text-xs text-slate-400">Esta tarea no tiene tests publicados; se linkeará a la lista de tareas.</p>
        ) : (
          <select value={selTestKey} onChange={handleTestChange} className={selectCls}>
            <option value="">(toda la tarea, sin test específico)</option>
            {tests.map((t) => (
              <option key={`${t._tipoKey}-${t.ID}`} value={`${t._tipoKey}-${t.ID}`}>
                {t.descripcion || `Test #${t.ID}`}
              </option>
            ))}
          </select>
        )
      )}
    </div>
  );
}

const WIZARD_STEPS = [
  { n: 1, label: 'Notificación' },
  { n: 2, label: 'Probar' },
  { n: 3, label: 'Confirmar' },
];

function StepIndicator({ step, maxStep, onStepClick }) {
  return (
    <div className="flex items-center gap-2 mb-5">
      {WIZARD_STEPS.map((s, i) => (
        <div key={s.n} className="flex items-center gap-2">
          <button
            type="button"
            disabled={s.n > maxStep}
            onClick={() => onStepClick(s.n)}
            className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded ${
              step === s.n
                ? 'bg-violet-600 text-white'
                : s.n <= maxStep
                  ? 'text-violet-700 hover:bg-violet-50 cursor-pointer'
                  : 'text-slate-300 cursor-default'
            }`}
          >
            <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] ${step === s.n ? 'bg-white text-violet-700' : 'bg-slate-200 text-slate-500'}`}>
              {s.n}
            </span>
            {s.label}
          </button>
          {i < WIZARD_STEPS.length - 1 && <span className="text-slate-300">—</span>}
        </div>
      ))}
    </div>
  );
}

function PushTab() {
  const [view, setView] = useState('list'); // 'list' | 'form'
  const [step, setStep] = useState(1);
  const [maxStep, setMaxStep] = useState(1);

  const [subsCount, setSubsCount]   = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [notifs, setNotifs]         = useState(null);
  const [tareaList, setTareaList]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const [title, setTitle]     = useState('');
  const [body, setBody]       = useState('');
  const [url, setUrl]         = useState('');
  const [urlMode, setUrlMode] = useState('texto'); // 'texto' | 'selector'
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [requireInteraction, setRequireInteraction] = useState(false);
  const [actions, setActions] = useState([{ title: '', url: '' }, { title: '', url: '' }]);
  const [sending, setSending]   = useState(false);
  const [sendMsg, setSendMsg]   = useState(null);

  const [testDeviceId, setTestDeviceId] = useState('');
  const [testing, setTesting] = useState(false);
  const [testMsg, setTestMsg] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [c, s, n, t] = await Promise.all([
        getPushSubscribersCount(), getPushSubscribers(), getPushNotifications(), getTareas(),
      ]);
      setSubsCount(c.count);
      setSubscribers(s.suscriptores);
      setNotifs(n.notificaciones);
      setTareaList(t);
      const testOnes = s.suscriptores.filter((sub) => sub.is_test);
      setTestDeviceId((prev) => (testOnes.some((sub) => sub.device_id === prev) ? prev : (testOnes[0]?.device_id ?? '')));
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateAction = (i, field, value) => {
    setActions((prev) => prev.map((a, idx) => (idx === i ? { ...a, [field]: value } : a)));
  };
  const activeActions = actions.filter((a) => a.title.trim());

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const up = await uploadAdminFile(file, 'push');
      setImageUrl(up.url);
    } catch (e) {
      alert('No se pudo subir la imagen: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setTitle(''); setBody(''); setUrl(''); setImageUrl(''); setRequireInteraction(false);
    setActions([{ title: '', url: '' }, { title: '', url: '' }]);
    setSendMsg(null); setTestMsg(null);
  };

  const openForm = () => {
    resetForm();
    setStep(1);
    setMaxStep(1);
    setView('form');
  };

  const handleRepeat = (n) => {
    setTitle(n.title || '');
    setBody(n.body || '');
    setUrl(n.url || '');
    setUrlMode('texto');
    setImageUrl(n.image_url || '');
    setRequireInteraction(!!n.require_interaction);
    setActions([0, 1].map((i) => {
      const a = (n.actions || [])[i];
      return a ? { title: a.title || '', url: a.url || '' } : { title: '', url: '' };
    }));
    setSendMsg(null);
    setTestMsg(null);
    setStep(1);
    setMaxStep(1);
    setView('form');
  };

  const backToList = () => {
    setView('list');
    load();
  };

  const goToStep = (n) => {
    if (n > maxStep) return;
    setStep(n);
  };

  const handleNextFromStep1 = () => {
    if (!title.trim() || !body.trim()) {
      alert('Título y mensaje son obligatorios');
      return;
    }
    setStep(2);
    setMaxStep((m) => Math.max(m, 2));
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      alert('Título y mensaje son obligatorios');
      return;
    }
    setSending(true);
    setSendMsg(null);
    try {
      const r = await createPushNotification({
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || undefined,
        image_url: imageUrl || undefined,
        require_interaction: requireInteraction,
        actions: activeActions,
      });
      setSendMsg(`Enviada a ${r.sent_ok_count} de ${r.targeted_count} dispositivos (${r.sent_failed_count} fallidas).`);
    } catch (e) {
      setSendMsg('Error al enviar: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  const handleSendTest = async () => {
    if (!testDeviceId) {
      alert('No hay ningún dispositivo suscripto para probar.');
      return;
    }
    if (!title.trim() || !body.trim()) {
      alert('Título y mensaje son obligatorios');
      return;
    }
    setTesting(true);
    setTestMsg(null);
    try {
      const r = await createPushNotification({
        title: title.trim(),
        body: body.trim(),
        url: url.trim() || undefined,
        image_url: imageUrl || undefined,
        require_interaction: requireInteraction,
        actions: activeActions,
        device_id: testDeviceId,
      });
      setTestMsg(
        r.sent_ok_count > 0
          ? 'Enviada. Revisá el dispositivo elegido.'
          : 'No se pudo entregar: la suscripción de ese dispositivo expiró o el permiso fue revocado.'
      );
      load();
    } catch (e) {
      setTestMsg('Error: ' + e.message);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="text-slate-500">Cargando…</div>;
  if (error) return <div className="text-red-600">Error: {error}</div>;

  const testCandidates = subscribers.filter((s) => s.is_test);
  const deviceStatusUrl = `${window.location.origin}/dispositivo-prueba`;
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(
    `Ayudame a probar las notificaciones del LABPSI: abrí este link desde tu celular y tocá "Marcar como dispositivo de prueba" → ${deviceStatusUrl}`
  )}`;

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(deviceStatusUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const showPreview = (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h2 className="text-sm font-semibold text-slate-700 mb-4">Vista previa</h2>
      <div className="grid gap-4">
        <PreviewFrame label="Chrome (escritorio)">
          <ChromePreview title={title} body={body} image={imageUrl} actions={actions} />
        </PreviewFrame>
        <PreviewFrame label="Android">
          <AndroidPreview title={title} body={body} image={imageUrl} actions={actions} />
        </PreviewFrame>
        <PreviewFrame label="iPhone">
          <IosPreview title={title} body={body} image={imageUrl} actions={actions} />
        </PreviewFrame>
      </div>
    </div>
  );

  if (view === 'list') {
    return (
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-700">Notificaciones enviadas</h2>
          <button
            onClick={openForm}
            className="px-4 py-2 rounded bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 cursor-pointer"
          >
            + Crear notificación
          </button>
        </div>

        {notifs.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
            Todavía no se enviaron notificaciones.
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4">
              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Título</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-600">Fecha</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Objetivo</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Entregadas</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Clics</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">CTR</th>
                    <th className="px-4 py-3 text-right font-medium text-slate-600">Fallidas</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {notifs.map((n) => {
                    const ctr = n.delivered_count > 0 ? Math.round(n.clicked_count / n.delivered_count * 100) : null;
                    return (
                      <tr key={n.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-slate-700">{n.title}</td>
                        <td className="px-4 py-3 text-slate-500">{formatFecha(n.created_at)}</td>
                        <td className="px-4 py-3 text-right text-slate-700">{n.targeted_count}</td>
                        <td className="px-4 py-3 text-right text-green-700">{n.delivered_count}</td>
                        <td className="px-4 py-3 text-right text-violet-700">{n.clicked_count}</td>
                        <td className="px-4 py-3 text-right text-slate-600">{ctr !== null ? `${ctr}%` : '—'}</td>
                        <td className="px-4 py-3 text-right text-red-500">{n.sent_failed_count}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => handleRepeat(n)}
                            className="text-xs text-violet-600 hover:text-violet-800 cursor-pointer whitespace-nowrap"
                          >
                            Repetir
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              "Entregada" significa que el navegador recibió el push y mostró la notificación; puede fallar si el
              dispositivo está sin conexión por mucho tiempo o si el usuario revocó el permiso de notificaciones.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <StepIndicator step={step} maxStep={maxStep} onStepClick={goToStep} />
        <button onClick={backToList} className="text-xs text-slate-400 hover:text-slate-600 cursor-pointer mb-4">
          Cancelar
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_260px] gap-4 items-start">
        <div className="bg-white rounded-xl border border-slate-200 p-5 @container">
          {step === 1 && (
            <div className="grid gap-4">
              <h2 className="text-sm font-semibold text-slate-700">Paso 1 · Armar la notificación</h2>
              <div className="grid grid-cols-1 @2xl:grid-cols-2 gap-x-6 gap-y-4">
                {/* ── Columna 1: Título + Mensaje + URL destino ── */}
                <div className="grid gap-3 content-start">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Título</label>
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                      maxLength={200}
                    />
                  </div>
                  <div>
                    <div className="flex items-baseline justify-between mb-1">
                      <label className="block text-xs text-slate-500">Mensaje</label>
                      <span className={`text-[11px] ${body.length >= BODY_MAX_LEN * 0.8 ? 'text-amber-500' : 'text-slate-400'}`}>
                        {body.length}/{BODY_MAX_LEN}
                      </span>
                    </div>
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                      rows={3}
                      maxLength={BODY_MAX_LEN}
                    />
                    <p className="text-xs text-slate-400 mt-1">
                      Los celulares y notebooks cortan el mensaje después de 2-3 líneas; por eso el límite es corto.
                    </p>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-xs text-slate-500">URL destino (opcional)</label>
                      <div className="flex gap-1 text-[11px]">
                        <button
                          type="button"
                          onClick={() => setUrlMode('texto')}
                          className={`px-2 py-0.5 rounded cursor-pointer ${urlMode === 'texto' ? 'bg-violet-100 text-violet-700 font-medium' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                          Escribir
                        </button>
                        <button
                          type="button"
                          onClick={() => setUrlMode('selector')}
                          className={`px-2 py-0.5 rounded cursor-pointer ${urlMode === 'selector' ? 'bg-violet-100 text-violet-700 font-medium' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                          Elegir destino
                        </button>
                      </div>
                    </div>
                    {urlMode === 'texto' ? (
                      <input
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="/"
                        className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                      />
                    ) : (
                      <DestinoUrlPicker tareaList={tareaList} onSelect={setUrl} />
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {url ? <>Se abrirá: <code className="text-slate-500">{url}</code></> : 'La página que se abre cuando tocan la notificación. Si la dejás vacía, abre la página principal.'}
                    </p>
                  </div>
                </div>

                {/* ── Columna 2: Imagen + opciones avanzadas ── */}
                <div className="grid gap-3 content-start">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Imagen (opcional)</label>
                    <div className="flex items-center gap-3">
                      <label className="inline-flex items-center gap-2 px-3 py-2 rounded border border-slate-300 bg-white text-sm text-slate-600 hover:bg-slate-50 hover:border-slate-400 cursor-pointer transition-colors">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="17 8 12 3 7 8" />
                          <line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        {uploading ? 'Subiendo…' : imageUrl ? 'Cambiar imagen' : 'Subir imagen'}
                        <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" disabled={uploading} />
                      </label>
                      {imageUrl && !uploading && (
                        <div className="flex items-center gap-2">
                          <img src={imageUrl} alt="" className="h-10 w-10 rounded border border-slate-200 object-cover" />
                          <button
                            onClick={() => setImageUrl('')}
                            className="text-xs text-slate-400 hover:text-red-500 cursor-pointer"
                          >
                            Quitar
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Se muestra junto al mensaje (Chrome/iOS) o como banner grande (Android).
                    </p>
                  </div>

                  <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={requireInteraction}
                      onChange={(e) => setRequireInteraction(e.target.checked)}
                      className="cursor-pointer"
                    />
                    Mantener visible hasta que la cierren
                  </label>
                  <p className="text-xs text-slate-400 -mt-2">
                    Solo tiene efecto en Chrome de escritorio; en celular las notificaciones ya quedan en la barra hasta que se descartan.
                  </p>

                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Botones de acción (opcional, hasta 2)</label>
                    <div className="grid gap-2">
                      {actions.map((a, i) => (
                        <div key={i} className="flex gap-2">
                          <input
                            value={a.title}
                            onChange={(e) => updateAction(i, 'title', e.target.value)}
                            placeholder={`Texto del botón ${i + 1}`}
                            maxLength={30}
                            className="w-1/2 border border-slate-300 rounded px-2 py-1.5 text-xs"
                          />
                          <input
                            value={a.url}
                            onChange={(e) => updateAction(i, 'url', e.target.value)}
                            placeholder="URL al tocarlo (opcional)"
                            className="w-1/2 border border-slate-300 rounded px-2 py-1.5 text-xs"
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-slate-400 mt-1">
                      Aparecen debajo del mensaje. Si no ponés una URL propia, usan la URL destino general.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleNextFromStep1}
                  className="px-4 py-2 rounded bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 cursor-pointer"
                >
                  Siguiente: Probar →
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="grid gap-4">
              <h2 className="text-sm font-semibold text-slate-700">Paso 2 · Enviar una prueba (opcional)</h2>
              <p className="text-xs text-slate-500 -mt-2">
                Mandala a un dispositivo de prueba propio para ver cómo queda de verdad antes de enviarla a todos.
              </p>

              <div className="flex items-center gap-2">
                {testCandidates.length > 0 ? (
                  <>
                    <select
                      value={testDeviceId}
                      onChange={(e) => setTestDeviceId(e.target.value)}
                      className="border border-slate-300 rounded px-2 py-1.5 text-xs text-slate-600"
                    >
                      {testCandidates.map((s) => (
                        <option key={s.device_id} value={s.device_id}>
                          {s.test_label || DEVICE_LABELS[s.device_type] || s.device_type} ({DEVICE_LABELS[s.device_type] ?? s.device_type})
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={handleSendTest}
                      disabled={testing}
                      className="px-3 py-1.5 rounded border border-violet-300 text-violet-700 text-xs font-medium hover:bg-violet-50 disabled:opacity-50 cursor-pointer"
                    >
                      {testing ? 'Enviando…' : 'Enviar de prueba'}
                    </button>
                  </>
                ) : (
                  <span className="text-xs text-slate-400">Todavía no marcaste ningún dispositivo como "de prueba".</span>
                )}
              </div>
              {testMsg && <p className="text-xs text-slate-600">{testMsg}</p>}

              <div className="border-t border-slate-200 pt-3">
                <p className="text-xs font-medium text-slate-600 mb-1.5">¿No tenés un dispositivo de prueba marcado todavía?</p>
                <ol className="text-xs text-slate-500 list-decimal list-inside space-y-1 mb-3">
                  <li>Abrí el link de abajo desde el celular o la computadora que querés usar de prueba.</li>
                  <li>Si ese dispositivo todavía no tiene las notificaciones activadas, la misma página te va a dejar activarlas ahí mismo.</li>
                  <li>Cargá un nombre para identificarlo (ej. "Mi celular") y tocá "Marcar como dispositivo de prueba".</li>
                  <li>Volvé a esta pantalla y actualizá: va a aparecer en el combo de arriba, listo para recibir pruebas.</li>
                </ol>

                <input
                  readOnly
                  value={deviceStatusUrl}
                  onFocus={(e) => e.target.select()}
                  className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 text-slate-600 bg-slate-50 mb-2"
                />

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-50 cursor-pointer whitespace-nowrap"
                  >
                    {linkCopied ? '¡Copiado!' : 'Copiar link'}
                  </button>
                  <a
                    href={whatsappShareUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs px-3 py-1.5 rounded bg-[#25D366] text-white hover:brightness-95 cursor-pointer inline-flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.29-1.39a9.9 9.9 0 0 0 4.75 1.21h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.51 2 12.04 2Zm5.8 14.02c-.24.68-1.4 1.3-1.93 1.34-.5.04-.98.24-3.32-.7-2.8-1.13-4.6-4.02-4.74-4.2-.14-.19-1.14-1.52-1.14-2.9s.72-2.06.98-2.34c.25-.28.55-.35.73-.35.18 0 .37 0 .53.01.17.01.4-.06.62.48.24.58.8 2 .87 2.15.07.15.11.32.02.51-.09.19-.14.31-.28.48-.14.17-.29.37-.42.5-.14.14-.28.29-.12.57.16.28.71 1.19 1.53 1.93 1.06.96 1.94 1.26 2.22 1.4.28.14.44.12.61-.07.17-.19.71-.83.9-1.11.19-.28.38-.24.63-.14.26.09 1.64.78 1.92.92.28.14.47.21.53.33.07.12.07.68-.17 1.36Z" />
                    </svg>
                    Enviar por WhatsApp
                  </a>
                </div>
              </div>

              <div className="flex justify-between pt-2 border-t border-slate-200">
                <button
                  onClick={() => setStep(1)}
                  className="px-4 py-2 rounded border border-slate-300 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer"
                >
                  ← Atrás
                </button>
                <button
                  onClick={() => { setStep(3); setMaxStep((m) => Math.max(m, 3)); }}
                  className="px-4 py-2 rounded bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 cursor-pointer"
                >
                  Siguiente: Confirmar →
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="grid gap-4">
              <h2 className="text-sm font-semibold text-slate-700">Paso 3 · Confirmar envío</h2>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-sm font-medium text-slate-800">{title}</p>
                <p className="text-sm text-slate-600 mt-0.5">{body}</p>
              </div>

              <p className="text-sm text-slate-600">
                Se enviará a <strong>{subsCount ?? 0}</strong> dispositivo{subsCount === 1 ? '' : 's'} activo{subsCount === 1 ? '' : 's'}.
                Esta acción no se puede deshacer.
              </p>

              {!sendMsg ? (
                <div className="flex justify-between pt-2 border-t border-slate-200">
                  <button
                    onClick={() => setStep(2)}
                    className="px-4 py-2 rounded border border-slate-300 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer"
                  >
                    ← Atrás
                  </button>
                  <button
                    onClick={handleSend}
                    disabled={sending}
                    className="px-4 py-2 rounded bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 disabled:opacity-50 cursor-pointer"
                  >
                    {sending ? 'Enviando…' : 'Confirmar y enviar'}
                  </button>
                </div>
              ) : (
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-sm text-slate-700 mb-3">{sendMsg}</p>
                  <div className="flex gap-2">
                    <button
                      onClick={backToList}
                      className="px-4 py-2 rounded bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 cursor-pointer"
                    >
                      Volver a la lista
                    </button>
                    <button
                      onClick={openForm}
                      className="px-4 py-2 rounded border border-slate-300 text-slate-600 text-sm font-medium hover:bg-slate-50 cursor-pointer"
                    >
                      Crear otra
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {showPreview}
      </div>
    </div>
  );
}
